import { Cartesian3 } from "cesium";
import { EmbankmentType, } from "../../../types";
import { EarthBrainUtils } from "../../../utilities/earthbrainutility";
import { checkPositionWrtToTileset } from "../../../utilities/local-utilities";
import { PathSlopeType, } from "../../mid-path/interface";
import { PathGeometry } from "../../mid-path/mid-path-geometry";
import { CornerSlopeStrategy } from "../../side-slope/cornor-slope-strategy";
import { SideSlopeGeometry } from "../../side-slope/side-slope-geometry";
import { getSideSlopeChunk } from "../../side-slope/utils";
import { CurveDataType } from "../../../core/alignment/horizontal-alignment/interface";
export class RoadGeometry {
    _id;
    _geometry;
    _pathGeometry;
    _sideSlopes;
    _slopeStrategy;
    get id() {
        return this._id;
    }
    get geometry() {
        return this._geometry;
    }
    get pathGeometry() {
        return this._pathGeometry;
    }
    get interpolatedPathInfos() {
        return this._pathGeometry.pathInfos;
    }
    constructor(id) {
        this._id = id;
        this._geometry = { indices: [], vertices: [] };
        this._sideSlopes = { left: [], right: [] };
        this._pathGeometry = new PathGeometry(this._id);
        this._slopeStrategy = new CornerSlopeStrategy();
    }
    dispose() {
        this._pathGeometry.dispose();
        this._sideSlopes.left.length = 0;
        this._sideSlopes.right.length = 0;
    }
    update(alignment, roadSetting, rubbingDestination) {
        this.dispose();
        // generate
        const centralLinePoints = alignment.centerLinePoints.map((point) => point.localPointWithAdjustedHeight);
        const infosOfPVI = alignment.getInfoOfPointOfVerticalIntersections();
        const mustHavePoints = infosOfPVI.map((pvi) => pvi.point);
        for (const curve of alignment.curvePoints) {
            if (curve.type === CurveDataType.LINEAR) {
                mustHavePoints.push(curve.points[0]);
                mustHavePoints.push(curve.points[curve.points.length - 1]);
            }
        }
        const pathSetting = this.getPathSettingsFromRoadSettings(roadSetting);
        this._pathGeometry.update(centralLinePoints, mustHavePoints, pathSetting, (positions, segmentIndices) => alignment.getDirectionsAlongPositions(positions, segmentIndices));
        rubbingDestination &&
            this.buildSideGeometry(rubbingDestination, roadSetting);
    }
    /**
     * geometry of surface in clockwise orientation
     */
    buildSideGeometry(rubbingDestination, settings) {
        const intersectionStrategy = (ray) => {
            const position = rubbingDestination.rayCast(ray);
            return position;
        };
        const getReferencePosition = (pos) => {
            return checkPositionWrtToTileset(pos, rubbingDestination);
        };
        //leftSide.
        let currentInfo = this._pathGeometry.pathInfos[0];
        const leftPointInfos = [];
        while (currentInfo) {
            for (const point of currentInfo.left) {
                if (leftPointInfos.length > 0) {
                    if (Cartesian3.equals(point, leftPointInfos[leftPointInfos.length - 1].position)) {
                        continue;
                    }
                }
                leftPointInfos.push({
                    position: point,
                    outwardDirection: currentInfo.direction.left,
                });
            }
            currentInfo = currentInfo.next;
        }
        const leftPathPoints = getSideSlopeChunk(leftPointInfos, getReferencePosition, false);
        for (const chunck of leftPathPoints) {
            let sideSlopeGeometry;
            if (chunck.isPointAbove) {
                sideSlopeGeometry = new SideSlopeGeometry({
                    pointInfos: chunck.pointInfos,
                    verticalDirection: new Cartesian3(0, 0, -1),
                    closeLoop: false,
                    generateShelf: settings.commonSetting.embarkmentSetting.generateShelf,
                    intersectionStrategy,
                    settings: settings.commonSetting.embarkmentSetting.left,
                });
            }
            else {
                sideSlopeGeometry = new SideSlopeGeometry({
                    pointInfos: chunck.pointInfos,
                    verticalDirection: new Cartesian3(0, 0, 1),
                    closeLoop: false,
                    generateShelf: settings.commonSetting.cutSetting.generateShelf,
                    intersectionStrategy,
                    settings: settings.commonSetting.cutSetting.left,
                });
            }
            sideSlopeGeometry.constructSlope(settings.commonSetting.path.interpolationPitch, this._slopeStrategy);
            sideSlopeGeometry.buildGeometry();
            this._sideSlopes.left.push({
                geometry: sideSlopeGeometry,
                isFill: chunck.isPointAbove,
            });
        }
        // connect chuncks
        for (let i = 0; i < this._sideSlopes.left.length - 1; ++i) {
            const firstPoint = this._sideSlopes.left[i].geometry.pointInfos[this._sideSlopes.left[i].geometry.pointInfos.length - 1].position;
            const nextPoint = this._sideSlopes.left[i + 1].geometry.pointInfos[0].position;
            const connectingPoint = new Cartesian3();
            Cartesian3.multiplyByScalar(Cartesian3.add(firstPoint, nextPoint, connectingPoint), 0.5, connectingPoint);
            this._sideSlopes.left[i].geometry.addConnectingPoint(connectingPoint, false);
            this._sideSlopes.left[i + 1].geometry.addConnectingPoint(connectingPoint, true);
        }
        // rightSide
        let currentRightInfo = this._pathGeometry.pathInfos[0];
        const rightPointInfos = [];
        while (currentRightInfo) {
            for (const point of currentRightInfo.right) {
                if (rightPointInfos.length > 0) {
                    if (Cartesian3.equals(point, rightPointInfos[rightPointInfos.length - 1].position)) {
                        continue;
                    }
                }
                rightPointInfos.push({
                    position: point,
                    outwardDirection: currentRightInfo.direction.right,
                });
            }
            currentRightInfo = currentRightInfo.next;
        }
        const rightPathPoints = getSideSlopeChunk(rightPointInfos, getReferencePosition, false);
        for (const chunck of rightPathPoints) {
            let sideSlopeGeometry;
            if (chunck.isPointAbove) {
                sideSlopeGeometry = new SideSlopeGeometry({
                    pointInfos: chunck.pointInfos,
                    verticalDirection: new Cartesian3(0, 0, -1),
                    closeLoop: false,
                    generateShelf: settings.commonSetting.embarkmentSetting.generateShelf,
                    intersectionStrategy,
                    settings: settings.commonSetting.embarkmentSetting.right,
                });
            }
            else {
                sideSlopeGeometry = new SideSlopeGeometry({
                    pointInfos: chunck.pointInfos,
                    verticalDirection: new Cartesian3(0, 0, 1),
                    closeLoop: false,
                    generateShelf: settings.commonSetting.cutSetting.generateShelf,
                    intersectionStrategy,
                    settings: settings.commonSetting.cutSetting.right,
                });
            }
            sideSlopeGeometry.constructSlope(settings.commonSetting.path.interpolationPitch, this._slopeStrategy);
            sideSlopeGeometry.buildGeometry();
            this._sideSlopes.right.push({
                geometry: sideSlopeGeometry,
                isFill: chunck.isPointAbove,
            });
        }
        // connect chuncks
        for (let i = 0; i < this._sideSlopes.right.length - 1; ++i) {
            const firstPoint = this._sideSlopes.right[i].geometry.pointInfos[this._sideSlopes.right[i].geometry.pointInfos.length - 1].position;
            const nextPoint = this._sideSlopes.right[i + 1].geometry.pointInfos[0].position;
            const connectingPoint = new Cartesian3();
            Cartesian3.multiplyByScalar(Cartesian3.add(firstPoint, nextPoint, connectingPoint), 0.5, connectingPoint);
            this._sideSlopes.right[i].geometry.addConnectingPoint(connectingPoint, false);
            this._sideSlopes.right[i + 1].geometry.addConnectingPoint(connectingPoint, true);
        }
    }
    getMergedGeometriesBySegmentSubType() {
        const result = [];
        const cutGeometry = {
            indices: [],
            vertices: [],
        };
        const fillGeometry = {
            indices: [],
            vertices: [],
        };
        const leftFills = this._sideSlopes.left.filter((s) => s.isFill);
        const leftCuts = this._sideSlopes.left.filter((s) => !s.isFill);
        const rightFills = this._sideSlopes.right.filter((s) => s.isFill);
        const rightCuts = this._sideSlopes.right.filter((s) => !s.isFill);
        for (const slope of leftFills) {
            EarthBrainUtils.mergeIGeom(fillGeometry, slope.geometry.geometry);
        }
        for (const slope of leftCuts) {
            EarthBrainUtils.mergeIGeom(cutGeometry, slope.geometry.geometry);
        }
        for (const slope of rightFills) {
            EarthBrainUtils.mergeIGeom(fillGeometry, slope.geometry.geometry);
        }
        for (const slope of rightCuts) {
            EarthBrainUtils.mergeIGeom(cutGeometry, slope.geometry.geometry);
        }
        if (this._pathGeometry.geometry.indices.length > 0) {
            result.push({
                id: this.id,
                embankmentType: EmbankmentType.NONE,
                geometry: this._pathGeometry.geometry,
            });
        }
        if (cutGeometry.indices.length > 0) {
            result.push({
                id: this.id,
                embankmentType: EmbankmentType.CUT,
                geometry: cutGeometry,
            });
        }
        if (fillGeometry.indices.length > 0) {
            result.push({
                id: this.id,
                embankmentType: EmbankmentType.FILL,
                geometry: fillGeometry,
            });
        }
        this._geometry.indices.length = 0;
        this._geometry.vertices.length = 0;
        EarthBrainUtils.mergeIGeom(this._geometry, this._pathGeometry.geometry);
        EarthBrainUtils.mergeIGeom(this._geometry, cutGeometry);
        EarthBrainUtils.mergeIGeom(this._geometry, fillGeometry);
        return result;
    }
    getPathSettingsFromRoadSettings(roadSetting) {
        const pathSetting = {
            commonSetting: roadSetting.commonSetting.path.roadSlopeType === PathSlopeType.CROSS
                ? {
                    interpolationPitch: roadSetting.commonSetting.path.interpolationPitch,
                    pathSlopeType: PathSlopeType.CROSS,
                    leftSlope: roadSetting.commonSetting.path.leftSlope,
                    leftWidth: roadSetting.commonSetting.path.leftWidth,
                    rightSlope: roadSetting.commonSetting.path.rightSlope,
                    rightWidth: roadSetting.commonSetting.path.rightWidth,
                }
                : {
                    interpolationPitch: roadSetting.commonSetting.path.interpolationPitch,
                    pathSlopeType: PathSlopeType.STRAIGHT,
                    slope: roadSetting.commonSetting.path.slope,
                    leftWidth: roadSetting.commonSetting.path.leftWidth,
                    rightWidth: roadSetting.commonSetting.path.rightWidth,
                },
            // segmentSettings: roadSetting.segmentSettings,
        };
        return pathSetting;
    }
    getMergedGeometriesBySide() {
        const result = {
            left: undefined,
            right: undefined,
            center: undefined,
        };
        const leftGeometry = {
            indices: [],
            vertices: [],
        };
        const rightGeometry = {
            indices: [],
            vertices: [],
        };
        const leftFills = this._sideSlopes.left.filter((s) => s.isFill);
        const leftCuts = this._sideSlopes.left.filter((s) => !s.isFill);
        const rightFills = this._sideSlopes.right.filter((s) => s.isFill);
        const rightCuts = this._sideSlopes.right.filter((s) => !s.isFill);
        for (const slope of leftFills) {
            EarthBrainUtils.mergeIGeom(leftGeometry, slope.geometry.geometry);
        }
        for (const slope of leftCuts) {
            EarthBrainUtils.mergeIGeom(leftGeometry, slope.geometry.geometry);
        }
        for (const slope of rightFills) {
            EarthBrainUtils.mergeIGeom(rightGeometry, slope.geometry.geometry);
        }
        for (const slope of rightCuts) {
            EarthBrainUtils.mergeIGeom(rightGeometry, slope.geometry.geometry);
        }
        if (this._pathGeometry.geometry.indices.length > 0) {
            result.center = this._pathGeometry.geometry;
        }
        if (leftGeometry.indices.length > 0) {
            result.left = leftGeometry;
        }
        if (rightGeometry.indices.length > 0) {
            result.right = rightGeometry;
        }
        return result;
    }
}
//# sourceMappingURL=road-geometry.js.map